import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Network, Plus, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGlobal } from "@/context/GlobalContext";
import { calcEjercicios, GF_DEF } from "@/lib/gastoFijo";
import { paletaVars } from "@/lib/gastoFijoPaletas";
import { GastoFijoTabla, GastoFijoComprobacion } from "@/components/control/GastoFijoTabla";
import { GastoFijoSemanas } from "@/components/control/GastoFijoSemanas";
import { ExportExcelButton } from "@/components/ExportExcelButton";
import { GastoFijoDialog } from "@/components/control/GastoFijoDialog";
import { VariablesGestion } from "@/components/control/VariablesGestion";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const msg = (t) => `${t} está disponible únicamente a nivel de planta o sucursal. Seleccione una planta o sucursal desde la estructura organizacional para visualizar su información.`;
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const card = "bg-white rounded-2xl border border-slate-200/70 shadow-sm";

export const Aviso = ({ text, testid }) => (
  <div className={`${card} py-16 px-6 flex flex-col items-center text-center gap-3`} data-testid={testid}>
    <Network className="w-8 h-8 text-red-500" /><p className="max-w-xl text-sm text-slate-600">{text}</p>
  </div>
);

export function MesPicker({ ym, setYm }) {
  const step = (d) => { const m = ym.month - 1 + d; setYm({ year: ym.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 + 1 }); };
  return (
    <div className="flex items-center gap-2" data-testid="gf-mes-picker">
      <CalendarDays className="w-4 h-4 text-slate-400" />
      <button onClick={() => step(-1)} data-testid="gf-mes-prev" className="h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50"><ChevronLeft className="w-4 h-4" /></button>
      <span className="w-44 text-center text-sm font-semibold text-slate-800" data-testid="gf-mes-label">{MESES[ym.month - 1]} {ym.year}</span>
      <button onClick={() => step(1)} data-testid="gf-mes-next" className="h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );
}

function Historial({ planta, structure, ym, setYm, def }) {
  const { data: all } = useQuery({
    queryKey: [def.mod, "historial", planta],
    queryFn: async () => (await api.get(`/control-semanal/${def.mod}/historial`, { params: { planta, structure } })).data.items,
  });
  const data = def.historialMes ? all?.filter((h) => h.year === ym.year && h.month === ym.month) : all;
  return (
    <div className={`${card} p-4`} data-testid="gf-historial">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Historial de ejercicios</p>
      {!data?.length ? <p className="text-sm text-slate-400">Sin ejercicios registrados.</p> : (
        <div className="flex flex-wrap gap-2">
          {data.map((h) => {
            const on = h.year === ym.year && h.month === ym.month;
            return (
              <button key={`${h.year}-${h.month}`} onClick={() => setYm({ year: h.year, month: h.month })} data-testid={`gf-hist-${h.year}-${h.month}`}
                className={`px-3 py-1.5 rounded-lg border text-xs font-semibold ${on ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                {MESES[h.month - 1]} {h.year} · {h.ejercicios} ejercicio{h.ejercicios > 1 ? "s" : ""}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Ejercicios({ planta, structure, canEdit, ym, setYm, puede, def }) {
  const qc = useQueryClient();
  const [dlg, setDlg] = useState(null);
  const [del, setDel] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: [def.mod, "ejercicios", planta, ym.year, ym.month],
    queryFn: async () => (await api.get(`/control-semanal/${def.mod}/ejercicios`, { params: { planta, structure, ...ym } })).data.items,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: [def.mod] });
  const onSaved = (e) => { setDlg(null); setYm({ year: e.year, month: e.month }); refresh(); };
  const borrar = async () => {
    try { await api.delete(`/control-semanal/${def.mod}/ejercicios/${del.id}`, { params: { structure } }); toast.success("Ejercicio eliminado."); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo eliminar."); }
    finally { setDel(null); }
  };
  const calc = data ? calcEjercicios(data, def) : [];
  return (
    <div className="space-y-4" data-testid={`gf-planta-${planta}`}>
      <div className={`${card} p-4 flex flex-wrap items-center gap-3`}>
        <MesPicker ym={ym} setYm={setYm} />
        <ExportExcelButton className="ml-auto" testid="gf-export" fileName={`${def.titulo}_${planta}_${ym.year}_${String(ym.month).padStart(2, "0")}`}
          titulo={`${planta} · ${MESES[ym.month - 1]} ${ym.year}`}
          hojas={[{ name: "Ejercicios", selector: '[data-testid="gf-table"]' }, { name: "Comprobación", selector: '[data-testid="gf-comprobacion"]' },
            ...(def.variables ? [{ name: "Variables de Gestión", selector: '[data-testid="gp-var-table"]' }] : []),
            ...(def.semanal ? [{ name: "Aplicación semanal", selector: '[data-testid="gf-semanas"]' }] : [])]} />
        {canEdit && <button onClick={() => setDlg({})} data-testid="gf-nuevo" className="h-9 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 flex items-center gap-2"><Plus className="w-4 h-4" />Nuevo ejercicio</button>}
      </div>
      <div className={`${card} p-5 space-y-4`}>
        <p className="text-center rounded-lg bg-[var(--gf-title)] py-1.5 text-lg font-bold text-white" data-testid="gf-planta-titulo">{planta}</p>
        {isLoading ? <div className="h-32 flex items-center justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>
          : !calc.length ? <p className="py-10 text-center text-sm text-slate-400" data-testid="gf-empty">Sin ejercicios en {MESES[ym.month - 1]} {ym.year}.</p>
          : <><GastoFijoTabla calc={calc} def={def} canEdit={canEdit} puede={puede} onEdit={(e) => setDlg({ edit: e })} onDelete={setDel} /><GastoFijoComprobacion calc={calc} def={def} />{def.semanal && !def.variables && <GastoFijoSemanas calc={calc} ym={ym} planta={planta} structure={structure} def={def} />}</>}
      </div>
      {def.variables && <VariablesGestion planta={planta} structure={structure} ym={ym} canEdit={canEdit && !!data} primero={!!data && !data.length} />}
      {def.semanal && def.variables && !isLoading && calc.length > 0 && <div className={`${card} p-5`} data-testid="gp-semanal-card"><GastoFijoSemanas calc={calc} ym={ym} planta={planta} structure={structure} def={def} /></div>}
      <Historial planta={planta} structure={structure} ym={ym} setYm={setYm} def={def} />
      <GastoFijoDialog def={def} open={!!dlg} onClose={() => setDlg(null)} planta={planta} structure={structure} ym={ym} edit={dlg?.edit} base={calc[calc.length - 1]} inicial={dlg?.edit ? data?.[0]?.id === dlg.edit.id && dlg.edit.inicio?.endsWith("-01") : !data?.length} onSaved={onSaved} />
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent data-testid="gf-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Eliminar último ejercicio</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará el ejercicio registrado el {del?.fecha_registro}. Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="gf-delete-cancel">Cancelar</AlertDialogCancel><AlertDialogAction onClick={borrar} className="bg-red-600 hover:bg-red-700" data-testid="gf-delete-confirm">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function GastoFijo({ def = GF_DEF }) {
  const { user } = useAuth();
  const { structureKey, node, currentStructure } = useGlobal();
  const canEdit = user?.is_admin || (user?.caps || []).includes(def.cap);
  const { data: cfg } = useQuery({ queryKey: [def.mod, "config"], queryFn: async () => (await api.get(`/control-semanal/${def.mod}/config`)).data });
  const [ymSel, setYm] = useState(null);
  const ym = ymSel || (cfg ? { year: cfg.year, month: cfg.month } : null);
  const act = cfg?.semana_actual;
  const puede = (e) => !!user?.is_admin || (!!act && e.week_year * 100 + e.week >= act.year * 100 + act.week);
  const { data, isLoading, error } = useQuery({
    queryKey: ["reporte", "nodo", structureKey, node?.id ?? "entire"],
    queryFn: async () => (await api.get("/control-semanal/reporte/nodo", { params: { structure: structureKey, node_id: node?.id } })).data,
    enabled: !!structureKey,
  });
  let body;
  if (isLoading || !ym) body = <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>;
  else if (error) body = <Aviso testid="gf-error" text={formatApiErrorDetail(error?.response?.data?.detail) || "No se pudo cargar el nodo seleccionado."} />;
  else if (!data?.is_leaf) body = <Aviso testid="gf-no-hoja" text={msg(def.mod === "gasto-fijo" ? "El Gasto Fijo" : `El módulo ${def.titulo}`)} />;
  else body = <Ejercicios key={data.planta} def={def} planta={data.planta} structure={structureKey} canEdit={canEdit} ym={ym} setYm={setYm} puede={puede} />;
  return (
    <div className="w-full space-y-6" data-testid="gasto-fijo" style={paletaVars()} data-export-root>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-red-600">Control Semanal</p>
        <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">{def.titulo}</h1>
        <p className="text-sm text-slate-500 mt-1">{def.descripcion} {currentStructure?.name ? `Estructura: ${currentStructure.name}${node ? ` · ${node.name}` : " · Toda la estructura"}` : ""}</p>
      </div>
      {body}
    </div>
  );
}
